import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { ShieldCheck, Lock, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ASSETS } from "@/data/products";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const AdminLogin = () => {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password required.");
      return;
    }
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Signed in.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sign-in failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--ptt-ink)] text-white flex items-center justify-center px-5 py-16 relative overflow-hidden" data-testid="admin-login-page">
      <div className="absolute inset-0 hairline-grid-dark opacity-50" />
      <Toaster position="top-right" richColors />
      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2.5 mb-8" data-testid="admin-login-home-link">
          <img src={ASSETS.logo} alt="PTT NZ" className="h-11 w-auto" />
        </Link>
        <div className="border border-white/15 bg-white/[0.04] backdrop-blur-sm p-7">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 grid place-items-center bg-[var(--ptt-teal)]/15 border border-[var(--ptt-teal)]/30">
              <ShieldCheck size={18} className="text-[var(--ptt-teal)]" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold">Admin sign in</h1>
              <p className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50 mt-0.5">Restricted access</p>
            </div>
          </div>
          <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="admin-login-form">
            <div>
              <label className="font-mono-tech text-[10px] uppercase tracking-widest text-white/60">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email"
                autoComplete="username"
                className="mt-1.5 w-full bg-transparent border border-white/20 focus:border-[var(--ptt-teal)] outline-none px-3 py-2.5 text-sm text-white"
                placeholder="admin@pttnz.net"
                required
              />
            </div>
            <div>
              <label className="font-mono-tech text-[10px] uppercase tracking-widest text-white/60">Password</label>
              <div className="relative mt-1.5">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password"
                  autoComplete="current-password"
                  className="w-full bg-transparent border border-white/20 focus:border-[var(--ptt-teal)] outline-none pl-9 pr-3 py-2.5 text-sm text-white"
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="ptt-button w-full px-5 py-3 text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {submitting ? "Signing in…" : <>Sign in <ArrowRight size={14} /></>}
            </button>
          </form>
          <p className="mt-5 font-mono-tech text-[10px] uppercase tracking-widest text-white/40">
            Sessions expire after 8 hours · NZ-based admin only
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
